import React from 'react';
import { getTpmsTireColor, TireStatus } from 'utils/tpms';
import TireTooltip from 'components/Tire/TireTooltip';
import BatteryIndicator from 'components/Tire/BatteryIndicator';
import type { TireInfo } from 'components/Tire/TireTooltip';

interface TireAlert {
  icon: string;
  color: string;
}

interface TireData {
  p1?: TireInfo;
  p2?: TireInfo;
  p3?: TireInfo;
  p4?: TireInfo;
  p5?: TireInfo;
  p6?: TireInfo;
  p7?: TireInfo;
  p8?: TireInfo;
  p9?: TireInfo;
  p10?: TireInfo;
}

interface TireValues {
  p1Status?: TireStatus;
  p2Status?: TireStatus;
  p3Status?: TireStatus;
  p4Status?: TireStatus;
  p5Status?: TireStatus;
  p6Status?: TireStatus;
  p7Status?: TireStatus;
  p8Status?: TireStatus;
  p9Status?: TireStatus;
  p10Status?: TireStatus;
  p1?: number;
  p2?: number;
  p3?: number;
  p4?: number;
  p5?: number;
  p6?: number;
  p7?: number;
  p8?: number;
  p9?: number;
  p10?: number;
  p1HasSensor?: boolean;
  p2HasSensor?: boolean;
  p3HasSensor?: boolean;
  p4HasSensor?: boolean;
  p5HasSensor?: boolean;
  p6HasSensor?: boolean;
  p7HasSensor?: boolean;
  p8HasSensor?: boolean;
  p9HasSensor?: boolean;
  p10HasSensor?: boolean;
  isDataStale?: boolean;
  p1IsStale?: boolean;
  p2IsStale?: boolean;
  p3IsStale?: boolean;
  p4IsStale?: boolean;
  p5IsStale?: boolean;
  p6IsStale?: boolean;
  p7IsStale?: boolean;
  p8IsStale?: boolean;
  p9IsStale?: boolean;
  p10IsStale?: boolean;
}

interface TireStatusMap {
  p1?: TireStatus;
  p2?: TireStatus;
  p3?: TireStatus;
  p4?: TireStatus;
  p5?: TireStatus;
  p6?: TireStatus;
  p7?: TireStatus;
  p8?: TireStatus;
  p9?: TireStatus;
  p10?: TireStatus;
}

interface BatteryLowMap {
  p1?: boolean;
  p2?: boolean;
  p3?: boolean;
  p4?: boolean;
  p5?: boolean;
  p6?: boolean;
  p7?: boolean;
  p8?: boolean;
  p9?: boolean;
  p10?: boolean;
}

interface ThreeAxlesTenWheelsProps {
  values: TireValues;
  tireData?: TireData;
  batteryLow?: BatteryLowMap;
  width?: number | string;
  height?: number | string;
  tireStatusMap?: TireStatusMap;
  tireStaleMap?: Record<string, boolean>;
  tireAlertMap?: Record<string, TireAlert>;
  onTireClick?: (tireCode: string) => void;
  tireNumberSystem?: string[];
}

// Tri-axle, 10 tires: front single (p1/p2), axle-2 dual (p3-p6), axle-3 dual
// (p7-p10). p-index -> tireCode order matches
// views/TirePressure/util.ts#getOrderedTireCodes for patternName '10x6'.
const TIRE_W = 14.7692;
const TIRE_H = 44.3077;
const ROW_GAP = 29.5385;
const FRONT_Y = 0;
const AXLE2_Y = FRONT_Y + TIRE_H + ROW_GAP; // 73.8462
const AXLE3_Y = AXLE2_Y + TIRE_H + ROW_GAP; // 147.6924
const VIEWBOX_H = AXLE3_Y + TIRE_H; // 192

const SINGLE_LEFT_X = 7.38462;
const SINGLE_RIGHT_X = 73.8462;
const OUTER_LEFT_X = 0;
const INNER_LEFT_X = 22.1538;
const INNER_RIGHT_X = 59.0769;
const OUTER_RIGHT_X = 81.2308;

const ThreeAxlesTenWheels: React.FC<ThreeAxlesTenWheelsProps> = ({
  values,
  tireData,
  batteryLow,
  width = 78,
  height = 192,
  tireStatusMap,
  tireStaleMap,
  tireAlertMap,
  onTireClick,
  tireNumberSystem,
}) => {
  const {
    p1Status,
    p2Status,
    p3Status,
    p4Status,
    p5Status,
    p6Status,
    p7Status,
    p8Status,
    p9Status,
    p10Status,
    isDataStale,
    p1IsStale,
    p2IsStale,
    p3IsStale,
    p4IsStale,
    p5IsStale,
    p6IsStale,
    p7IsStale,
    p8IsStale,
    p9IsStale,
    p10IsStale,
  } = values;

  const renderTireAlert = (key: string, x: number, y: number) => {
    const alert = tireAlertMap?.[key];
    if (!alert) return null;

    const isPlayIcon = alert.icon.includes('▶');
    const alertFontSize = isPlayIcon ? (alert.icon.length > 1 ? 6 : 9) : 16;

    return (
      <g style={{ pointerEvents: 'none' }}>
        <circle
          cx={x}
          cy={y}
          r="8"
          fill={alert.color}
          stroke="#FFFFFF"
          strokeWidth="1.8"
        />
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#FFFFFF"
          fontSize={alertFontSize}
          fontWeight="900"
        >
          {alert.icon}
        </text>
      </g>
    );
  };

  const tire = (
    key: keyof TireData,
    x: number,
    y: number,
    status: TireStatus | undefined,
    isStale: boolean | undefined,
    index: number
  ) => (
    <TireTooltip tireInfo={tireData?.[key]}>
      <g
        onClick={() => {
          if (onTireClick && tireNumberSystem) {
            onTireClick(tireNumberSystem[index]);
          }
        }}
        style={{ cursor: onTireClick ? 'pointer' : 'default' }}
      >
        <rect
          x={x}
          y={y}
          width={TIRE_W}
          height={TIRE_H}
          rx="2.80697"
          fill={getTpmsTireColor(
            tireStatusMap?.[key] ?? status ?? TireStatus.NO_SENSOR,
            tireStaleMap?.[key] ?? isStale ?? isDataStale
          )}
        />
        <BatteryIndicator
          cx={x + TIRE_W / 2}
          cy={y + TIRE_H / 2}
          tireBottomY={y + TIRE_H}
          tireWidth={TIRE_W}
          show={batteryLow?.[key]}
        />
        {renderTireAlert(key, x + TIRE_W / 2, y + TIRE_H / 2)}
      </g>
    </TireTooltip>
  );

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 96 ${VIEWBOX_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Center spine */}
      <line
        x1="48"
        y1={FRONT_Y + TIRE_H / 2}
        x2="48"
        y2={AXLE3_Y + TIRE_H / 2}
        stroke="#4A5C72"
        strokeWidth="7.38462"
      />

      {/* Front axle */}
      <line
        x1={INNER_LEFT_X}
        y1={FRONT_Y + TIRE_H / 2}
        x2={SINGLE_RIGHT_X + TIRE_W}
        y2={FRONT_Y + TIRE_H / 2}
        stroke="#4A5C72"
        strokeWidth="7.38462"
      />

      {/* Axle 2 */}
      <line
        x1={OUTER_LEFT_X + TIRE_W}
        y1={AXLE2_Y + TIRE_H / 2}
        x2={OUTER_RIGHT_X}
        y2={AXLE2_Y + TIRE_H / 2}
        stroke="#4A5C72"
        strokeWidth="7.38462"
      />

      {/* Axle 3 */}
      <line
        x1={OUTER_LEFT_X + TIRE_W}
        y1={AXLE3_Y + TIRE_H / 2}
        x2={OUTER_RIGHT_X}
        y2={AXLE3_Y + TIRE_H / 2}
        stroke="#4A5C72"
        strokeWidth="7.38462"
      />

      {/* Front-Left (0x17) / Front-Right (0x19) */}
      {tire('p1', SINGLE_LEFT_X, FRONT_Y, p1Status, p1IsStale, 0)}
      {tire('p2', SINGLE_RIGHT_X, FRONT_Y, p2Status, p2IsStale, 1)}

      {/* Middle-Inside-Left (0x27) / Middle-Inside-Right (0x29) */}
      {tire('p3', INNER_LEFT_X, AXLE2_Y, p3Status, p3IsStale, 2)}
      {tire('p4', INNER_RIGHT_X, AXLE2_Y, p4Status, p4IsStale, 3)}
      {/* Middle-Outside-Left (0x26) / Middle-Outside-Right (0x2A) */}
      {tire('p5', OUTER_LEFT_X, AXLE2_Y, p5Status, p5IsStale, 4)}
      {tire('p6', OUTER_RIGHT_X, AXLE2_Y, p6Status, p6IsStale, 5)}

      {/* Rear-Inside-Left (0x37) / Rear-Inside-Right (0x39) */}
      {tire('p7', INNER_LEFT_X, AXLE3_Y, p7Status, p7IsStale, 6)}
      {tire('p8', INNER_RIGHT_X, AXLE3_Y, p8Status, p8IsStale, 7)}
      {/* Rear-Outside-Left (0x36) / Rear-Outside-Right (0x3A) */}
      {tire('p9', OUTER_LEFT_X, AXLE3_Y, p9Status, p9IsStale, 8)}
      {tire('p10', OUTER_RIGHT_X, AXLE3_Y, p10Status, p10IsStale, 9)}
    </svg>
  );
};

export default ThreeAxlesTenWheels;
